import { createFileRoute, useNavigate, type ErrorComponentProps } from '@tanstack/react-router'
import { useId } from 'react'

import Skeleton from '@/components/ui/Skeleton'
import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import { tierName } from '@/features/account/plan'
import { PLAN_PRESENTATION } from '@/features/onboarding/plans'
import { paymentMethodName } from '@/features/payments/lib/gateways'
import {
  useSuspensePayments,
  useSuspensePlans,
  useSuspenseSubscriptionMe,
} from '@/features/subscriptions/api/queries'
import { findPlan, periodPhrase } from '@/features/subscriptions/lib/catalog'
import { planSummary } from '@/features/subscriptions/lib/features'
import { formatBsDate } from '@/lib/bsDate'
import { knownTierSchema } from '@/schemas/subscriptions'
import AccountPageHeader from '@/screens/account/components/AccountPageHeader'
import { rupees } from '@/screens/account/components/checkout/checkoutModel'
import ManagePlanHeaderCard, {
  type ManagePlanInfoRow,
} from '@/screens/account/components/manage/ManagePlanHeaderCard'
import ManageFeatureList from '@/screens/account/components/manage/ManageFeatureList'
import BillingHistoryDialog from '@/screens/account/components/manage/BillingHistoryDialog'
import YourPlanCard from '@/screens/account/components/manage/YourPlanCard'
import { loadManagePlan } from '@/screens/account/ManagePlanScreen.loader'
import { billingSearchSchema } from '@/screens/account/subscription-search'

const HEADER = <AccountPageHeader title="Manage plan" />

/**
 * Shaped like the loaded page so nothing reflows when the three MAIN reads
 * land: the header card's avatar/name/tagline/badge/button row, three kv
 * skeleton rows under it, one "Your plan" row, and the feature checklist's
 * own title plus a handful of lines.
 */
function ManagePlanPending() {
  return (
    <ScreenLayout pending pendingLabel="Loading your plan…" header={HEADER} fullWidth>
      <section className="flex flex-col gap-2 bg-card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Skeleton className="size-14 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1">
            <div className="flex h-7 items-center">
              <Skeleton className="h-5 w-24 rounded-sm" />
            </div>
            <div className="flex h-5 items-center">
              <Skeleton className="h-4 w-48 rounded-xs" />
            </div>
          </div>
          <Skeleton className="h-9 w-28 shrink-0" />
        </div>
        <div className="divide-y divide-border-subtle border-t border-border-subtle">
          {['Price', 'Active until', 'Paid with'].map((label) => (
            <div key={label} className="flex h-13 items-center justify-between gap-3">
              <Skeleton className="h-4 w-20 rounded-xs" />
              <Skeleton className="h-4 w-32 rounded-xs" />
            </div>
          ))}
        </div>
      </section>

      <section aria-hidden="true" className="bg-card px-4 pt-3 pb-2">
        <Skeleton className="h-3 w-16 rounded-xs" />
        <div className="flex h-15 items-center justify-between gap-3">
          <Skeleton className="h-5 w-32 rounded-sm" />
          <Skeleton className="h-4 w-16 rounded-xs" />
        </div>
      </section>

      <section className="flex flex-col gap-2 bg-card p-4">
        <Skeleton className="h-3 w-40 rounded-xs" />
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-5 w-full max-w-72 rounded-xs" />
        ))}
      </section>
    </ScreenLayout>
  )
}

function ManagePlanError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Couldn't load your plan"
      description="Check your connection and try again."
    />
  )
}

export const Route = createFileRoute('/_app/subscription/manage')({
  validateSearch: billingSearchSchema,
  loaderDeps: ({ search }) => ({ billing: search.billing }),
  loader: loadManagePlan,
  pendingComponent: ManagePlanPending,
  errorComponent: ManagePlanError,
  component: ManagePlanScreen,
})

/**
 * Manage plan — Figma `4243:77743`, paid members only (the loader redirects a
 * Free member to `/subscription` before this ever renders — see
 * `ManagePlanScreen.loader.ts`).
 *
 * Three approved deviations from the frame, all from BUILD-BRIEF:
 *
 *  - No "Payment method" or "Cancel plan" rows, and no cancellation footnote —
 *    neither has an endpoint (`YourPlanCard`'s own doc goes further).
 *  - "Renews on" → "Active until"; no "Member since" (`ManagePlanHeaderCard`'s
 *    own doc).
 *  - The avatar art at its real 56px slot, not Figma's oversized render of
 *    this specific node.
 *
 * `subscriptionMe`/`plans`/`payments` are all MAIN (the loader), so this body
 * only ever runs once all three are in cache — no inline loading branches.
 */
function ManagePlanScreen() {
  const { data: entitlement } = useSuspenseSubscriptionMe()
  const { data: plans } = useSuspensePlans()
  const { data: payments } = useSuspensePayments()
  const { billing } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const giftsId = useId()

  const tier = tierName(entitlement.tier)
  const knownTier = knownTierSchema.safeParse(entitlement.tier)
  const presentation = PLAN_PRESENTATION[knownTier.success ? knownTier.data : 'free']
  const tagline = planSummary(entitlement.features)

  const plan = entitlement.planId === null ? null : findPlan(plans, entitlement.planId)
  // Latest Completed payment: `payments/` is already newest-first (CONTRACT §1.4).
  const latestCompleted = payments.find((payment) => payment.status === 'Completed') ?? null
  const paidWith = latestCompleted === null ? null : paymentMethodName(latestCompleted.method)
  const receiptCount = payments.filter((payment) => payment.status === 'Completed').length

  const rows: ManagePlanInfoRow[] = []
  // Omitted when the plan can't be joined (brief's own rule) — never an invented price.
  if (plan !== null) {
    rows.push({
      label: 'Price',
      value: `${rupees(plan.price.total)} ${periodPhrase(plan.periodDays)}`,
    })
  }
  rows.push({ label: 'Active until', value: formatBsDate(entitlement.activeUntil) })
  if (paidWith !== null) rows.push({ label: 'Paid with', value: paidWith })

  return (
    <ScreenLayout header={HEADER} fullWidth>
      <ManagePlanHeaderCard tier={tier} art={presentation.art} tagline={tagline} rows={rows} />

      <YourPlanCard
        receiptCount={receiptCount}
        onOpenBilling={() => {
          void navigate({ search: { billing: true } })
        }}
      />

      {/* Mock "What it gives you": the surface fill 16 in, a 12/600 head, white ticks. */}
      <section aria-labelledby={giftsId} className="flex flex-col gap-2 bg-card p-4">
        <h2 id={giftsId} className="text-h-xs text-muted-foreground-subtle">
          What {tier} gives you
        </h2>
        <ManageFeatureList features={entitlement.features} />
      </section>

      <BillingHistoryDialog
        open={billing === true}
        onOpenChange={(open) => {
          void navigate({ search: { billing: open ? true : undefined }, replace: !open })
        }}
      />
    </ScreenLayout>
  )
}
